/**
 * Horizontally scrollable image cards for the first product option.
 *
 * The markup is a plain fieldset of radio inputs carrying every data attribute
 * variant-picker.js reads, so selection, variant resolution, the section
 * re-render and the gallery sync are handled entirely by the theme. This
 * element only adds the three things a scroller needs and CSS cannot express:
 * keeping the selected card in view, remembering the scroll offset across the
 * section re-render, and telling CSS whether there is more content to scroll.
 *
 * On mobile the row stays a pure touch surface (swipe, edge fade as the only
 * affordance) -- no arrows there, per the original design. On desktop there
 * is no swipe gesture and a mouse wheel does not scroll a horizontal
 * container, so a mouse-only shopper has no way to reach later cards without
 * dragging the (hidden) scrollbar. Previous/next arrow buttons are added for
 * that case: rendered in the markup, wired up here, and shown/hidden purely
 * through the existing data-overflow state (CSS in
 * variant-image-cards-styles.liquid reads the same attribute), so there is a
 * single source of truth for "is there more to scroll, and in which
 * direction".
 */

/** Scroll offsets survive the morph even in the case where it replaces the element. */
const scrollOffsets = new Map();

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

class VariantImageCards extends HTMLElement {
  /** @type {HTMLElement | null} */
  #row = null;

  /** @type {HTMLButtonElement | null} */
  #prevArrow = null;

  /** @type {HTMLButtonElement | null} */
  #nextArrow = null;

  #frame = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MutationObserver | undefined} */
  #mutationObserver;

  connectedCallback() {
    const row = this.querySelector('.variant-cards__row');
    if (!(row instanceof HTMLElement)) return;
    this.#row = row;

    row.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('change', this.#onChange);

    const prevArrow = this.querySelector('.variant-cards__arrow--prev');
    const nextArrow = this.querySelector('.variant-cards__arrow--next');

    if (prevArrow instanceof HTMLButtonElement) {
      this.#prevArrow = prevArrow;
      prevArrow.addEventListener('click', () => this.#scrollByCard(-1));
    }

    if (nextArrow instanceof HTMLButtonElement) {
      this.#nextArrow = nextArrow;
      nextArrow.addEventListener('click', () => this.#scrollByCard(1));
    }

    // The element normally survives the morph (it carries a data-key), in which
    // case scrollLeft is already correct and nothing needs restoring. The
    // stored offset only matters when the morph does replace it -- otherwise
    // choosing a size would silently rewind the colour row to the far left.
    const stored = scrollOffsets.get(this.dataset.cardsId ?? '');

    if (stored) {
      this.#withoutSmoothScroll(() => {
        row.scrollLeft = stored;
      });
    } else {
      // Wait a frame: on first paint the card images have not been laid out, so
      // measuring now would produce a no-op.
      requestAnimationFrame(() => this.#reveal(false));
    }

    this.#sync();

    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(row);

    // The morph rewrites children and can drop data-overflow, so recompute
    // whenever the subtree changes. #sync writes only when the value actually
    // differs, which stops the observer from feeding itself.
    this.#mutationObserver = new MutationObserver(this.#schedule);
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-overflow', 'checked', 'data-current-checked'],
    });
  }

  disconnectedCallback() {
    this.#row?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('change', this.#onChange);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    if (this.#row) scrollOffsets.set(this.dataset.cardsId ?? '', this.#row.scrollLeft);
    this.#schedule();
  };

  #onChange = () => {
    this.#reveal(true);
    // The section re-render lands a moment after the change event; re-measure
    // once it has, in case availability changed the row.
    setTimeout(() => this.#sync(), 400);
  };

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  /**
   * Runs a scroll write without the row's smooth scrolling, so restoring a
   * remembered offset does not look like the row sliding on its own.
   * @param {() => void} write
   */
  #withoutSmoothScroll(write) {
    const row = this.#row;
    if (!row) return;

    const previous = row.style.scrollBehavior;
    row.style.scrollBehavior = 'auto';
    write();
    row.style.scrollBehavior = previous;
  }

  /**
   * Moves the row by roughly one card's width (plus gap) per arrow click.
   * Falls back to the row's own width when a card's size cannot be read yet.
   * @param {1 | -1} direction
   */
  #scrollByCard(direction) {
    const row = this.#row;
    if (!row) return;

    const firstCard = row.querySelector('.variant-card');
    const cardWidth = firstCard instanceof HTMLElement ? firstCard.getBoundingClientRect().width : row.clientWidth;
    const gap = parseFloat(getComputedStyle(row).columnGap || '0') || 0;
    // Move by roughly two cards at a time so the click has a visible effect
    // even on a wide desktop row, while still stopping short of a full jump.
    const distance = (cardWidth + gap) * 2;

    row.scrollBy({
      left: direction * distance,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /** Tells CSS which edge fade to show, and which arrows are usable. */
  #sync() {
    const row = this.#row;
    if (!row) return;

    const max = row.scrollWidth - row.clientWidth;
    const overflow =
      max <= 1 ? 'none' : row.scrollLeft <= 1 ? 'start' : row.scrollLeft >= max - 1 ? 'end' : 'middle';

    if (this.dataset.overflow !== overflow) this.dataset.overflow = overflow;
  }

  /**
   * Brings the checked card into view.
   *
   * Deliberately not scrollIntoView: that also scrolls the nearest scrollable
   * vertical ancestor, so landing on a ?variant= link for the eighth colour
   * would jump the page down to the picker. Adjusting scrollLeft directly
   * cannot move the page.
   *
   * @param {boolean} smooth
   */
  #reveal(smooth) {
    const row = this.#row;
    if (!row) return;

    const checked = row.querySelector('input:checked');
    const card = checked?.closest('.variant-card');
    if (!(card instanceof HTMLElement)) return;

    const rowBox = row.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const margin = 16;

    let delta = 0;
    if (cardBox.left < rowBox.left + margin) {
      delta = cardBox.left - rowBox.left - margin;
    } else if (cardBox.right > rowBox.right - margin) {
      delta = cardBox.right - rowBox.right + margin;
    }

    if (!delta) return;

    row.scrollBy({ left: delta, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
  }
}

if (!customElements.get('variant-image-cards')) {
  customElements.define('variant-image-cards', VariantImageCards);
}
